import { useCallback, useEffect, useRef } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { useLatestVersion } from "./useLatestVersion";
import { isLaterVersion } from "./compareVersions";
import { UPDATE_POLL_INTERVAL_MS } from "./updatePolling";

/**
 * `coming` is the API reporting a later release before the worker has found it. The web
 * and API deploy in parallel, so it can also mean the web deploy has not landed yet;
 * either way there is nothing to install, so it is said but never offered.
 */
export type UpdateState = "none" | "coming" | "available";

/**
 * How long after a launch an update may be applied without asking. Registration
 * starts on the first render and a worker held over from the last session is offered
 * a moment later, so this only has to outlast a slow phone getting that far.
 */
const COLD_START_GRACE_MS = 15 * 1000;

/**
 * One silent reload per session. A worker that never takes control would otherwise be
 * offered again by the document it just reloaded, and boot the app in a loop.
 */
const AUTO_APPLY_GUARD_KEY = "lbc-hub-update-auto-applied";

// storage that cannot be read counts as spent, because a guard that cannot be read cannot hold
const hasAutoApplied = () => {
    try {
        return sessionStorage.getItem(AUTO_APPLY_GUARD_KEY) !== null;
    } catch {
        return true;
    }
};

const markAutoApplied = () => {
    try {
        sessionStorage.setItem(AUTO_APPLY_GUARD_KEY, "1");
    } catch {
        // hasAutoApplied already refuses when storage is unavailable
    }
};

/**
 * Offers an update, and applies one only at a launch nobody has touched yet.
 *
 * With `registerType: "prompt"` the waiting worker stays waiting until something sends
 * it SKIP_WAITING. The browser is meant to activate it once every client in scope has
 * closed, but Android freezes a swiped-away PWA's document and resumes it, so that
 * never fires and a phone can sit on one build indefinitely.
 */
export const useServiceWorkerUpdate = () => {
    const localVersion = import.meta.env.VITE_RELEASE_VERSION || "dev";
    const { data: latestVersion } = useLatestVersion();

    const registrationRef = useRef<{ swUrl: string; registration: ServiceWorkerRegistration }>();
    const touchedRef = useRef(false);

    const {
        needRefresh: [needRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        onRegisteredSW(swUrl, registration) {
            if (registration) registrationRef.current = { swUrl, registration };
        },

        onRegisterError(error) {
            // eslint-disable-next-line no-console
            console.error("Service worker registration error:", error);
        },
    });

    useEffect(() => {
        const noteInteraction = () => {
            touchedRef.current = true;
        };

        window.addEventListener("pointerdown", noteInteraction, { capture: true, once: true });
        window.addEventListener("keydown", noteInteraction, { capture: true, once: true });

        return () => {
            window.removeEventListener("pointerdown", noteInteraction, { capture: true });
            window.removeEventListener("keydown", noteInteraction, { capture: true });
        };
    }, []);

    const checkForUpdate = useCallback(async () => {
        const current = registrationRef.current;
        if (!current || !navigator.onLine) return;

        try {
            /**
             * Calling update() while the script is missing would unregister the
             * worker and leave the app with no cache at all, so confirm it is
             * still being served before asking.
             */
            const response = await fetch(current.swUrl, { cache: "no-store" });

            if (response.status === 200) await current.registration.update();
        } catch {
            // a check that fails is only a check that happens later
        }
    }, []);

    useEffect(() => {
        const interval = setInterval(checkForUpdate, UPDATE_POLL_INTERVAL_MS);

        const checkOnReturn = () => {
            if (document.visibilityState === "visible") void checkForUpdate();
        };

        document.addEventListener("visibilitychange", checkOnReturn);

        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", checkOnReturn);
        };
    }, [checkForUpdate]);

    // web ahead of the API is the same parallel deploy seen from the other side, so only this way round counts
    const laterRelease = isLaterVersion(latestVersion, localVersion);
    const coming = !needRefresh && laterRelease;

    // the API knows a release is out, so ask the worker now rather than at the next poll
    useEffect(() => {
        if (coming) void checkForUpdate();
    }, [coming, checkForUpdate]);

    /**
     * An untouched launch is the one moment there is nothing to lose, so the update is
     * applied rather than offered and the reload lands while the app is still starting.
     * Untouched is the condition that makes it safe; the window only stops a page left
     * open all day from reloading under somebody who is reading it.
     */
    useEffect(() => {
        if (!needRefresh || touchedRef.current) return;
        if (performance.now() > COLD_START_GRACE_MS || hasAutoApplied()) return;

        markAutoApplied();
        void updateServiceWorker(true);
    }, [needRefresh, updateServiceWorker]);

    /**
     * `updateServiceWorker` sends SKIP_WAITING, and vite-plugin-pwa reloads the page
     * itself once the new worker takes control, so there is nothing to chase here
     * afterwards.
     */
    const applyUpdate = useCallback(() => {
        void updateServiceWorker(true);
    }, [updateServiceWorker]);

    const update: UpdateState = needRefresh ? "available" : coming ? "coming" : "none";

    return {
        update,
        laterRelease,
        applyUpdate,
        localVersion,
        latestVersion,
    };
};
